/**
 * Slim currency helpers for floorplanner price display.
 * Catalog prices stay SEK; convert for display via currencyRates.
 */

export type Currency =
  | "AUD"
  | "BGN"
  | "BRL"
  | "CAD"
  | "CHF"
  | "CNY"
  | "CZK"
  | "DKK"
  | "EUR"
  | "GBP"
  | "HKD"
  | "HUF"
  | "IDR"
  | "ILS"
  | "INR"
  | "ISK"
  | "JPY"
  | "KRW"
  | "KWD"
  | "MXN"
  | "MYR"
  | "NOK"
  | "NZD"
  | "PHP"
  | "PLN"
  | "RON"
  | "SAR"
  | "SEK"
  | "SGD"
  | "THB"
  | "TRY"
  | "USD"
  | "ZAR";

export const SUPPORTED_CURRENCIES: Currency[] = [
  "AUD",
  "BGN",
  "BRL",
  "CAD",
  "CHF",
  "CNY",
  "CZK",
  "DKK",
  "EUR",
  "GBP",
  "HKD",
  "HUF",
  "IDR",
  "ILS",
  "INR",
  "ISK",
  "JPY",
  "KRW",
  "KWD",
  "MXN",
  "MYR",
  "NOK",
  "NZD",
  "PHP",
  "PLN",
  "RON",
  "SAR",
  "SEK",
  "SGD",
  "THB",
  "TRY",
  "USD",
  "ZAR",
];

const PREFERRED_CURRENCY_CODES: Currency[] = [
  "SEK",
  "EUR",
  "USD",
  "GBP",
  "DKK",
  "NOK",
  "KRW",
];

const preferredSet = new Set(PREFERRED_CURRENCY_CODES);

export const DEFAULT_FLOORPLAN_CURRENCY: Currency = "SEK";

/** Preferred currencies first, then the rest. */
export function getCurrenciesWithPreferredFirst(): Currency[] {
  const preferred = PREFERRED_CURRENCY_CODES.filter((c) =>
    SUPPORTED_CURRENCIES.includes(c),
  );
  const rest = SUPPORTED_CURRENCIES.filter((c) => !preferredSet.has(c));
  return [...preferred, ...rest];
}

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  AUD: "A$",
  BGN: "лв",
  BRL: "R$",
  CAD: "C$",
  CHF: "CHF",
  CNY: "¥",
  CZK: "Kč",
  DKK: "DKK",
  EUR: "€",
  GBP: "£",
  HKD: "HK$",
  HUF: "Ft",
  IDR: "Rp",
  ILS: "₪",
  INR: "₹",
  ISK: "ISK",
  JPY: "¥",
  KRW: "₩",
  KWD: "د.ك",
  MXN: "$",
  MYR: "RM",
  NOK: "NOK",
  NZD: "NZ$",
  PHP: "₱",
  PLN: "zł",
  RON: "lei",
  SAR: "﷼",
  SEK: "SEK",
  SGD: "S$",
  THB: "฿",
  TRY: "₺",
  USD: "$",
  ZAR: "R",
};

export function getCurrencyDisplaySymbol(currency: Currency): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

export function roundFiat(amount: number, currency: Currency): number {
  if (currency === "KRW") return Math.round(amount);
  return Number(amount.toFixed(2));
}

function buildRatesMapWithBase(
  ratesArray: Array<{ currency: string; rate: number }>,
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const { currency, rate } of ratesArray) {
    map[currency] = rate;
  }
  return map;
}

/**
 * Exchange rate from SEK to display currency: 1 SEK = rate displayCurrency.
 */
export function getSekToDisplayRate(
  ratesArray: Array<{ currency: string; rate: number }>,
  base: string,
  displayCurrency: string,
): number | null {
  if (displayCurrency === "SEK") return null;
  const map = buildRatesMapWithBase(ratesArray);
  if (base === "SEK") {
    return map[displayCurrency] ?? null;
  }
  const sekRate = map["SEK"];
  const displayRate = map[displayCurrency];
  if (sekRate == null || displayRate == null) return null;
  return displayRate / sekRate;
}

/**
 * Convert a SEK catalog amount to the floorplan display currency.
 */
export function convertSekToDisplayAmount(
  priceSek: number,
  displayCurrency: string,
  ratesArray: Array<{ currency: string; rate: number }> | null | undefined,
  base: string | null | undefined,
): number {
  if (displayCurrency === "SEK" || !ratesArray?.length || !base) return priceSek;
  const rate = getSekToDisplayRate(ratesArray, base, displayCurrency);
  if (rate == null) return priceSek;
  return roundFiat(priceSek * rate, displayCurrency as Currency);
}

/**
 * Convert an amount from any supported currency to the floorplan display currency.
 * Rates are relative to `base` (1 base = rate units of that currency).
 * Returns null when conversion is not possible (caller can fall back to source).
 */
export function convertAmountToDisplayCurrency(
  amount: number,
  fromCurrency: string,
  displayCurrency: string,
  ratesArray: Array<{ currency: string; rate: number }> | null | undefined,
  base: string | null | undefined,
): number | null {
  if (!Number.isFinite(amount)) return null;
  const from = normalizeCurrency(fromCurrency);
  const to = normalizeCurrency(displayCurrency);
  if (from === to) return amount;
  if (!ratesArray?.length || !base) return null;

  const map = buildRatesMapWithBase(ratesArray);
  if (base === "SEK") {
    // Rates mean: 1 SEK = rate[currency].
    if (from === "SEK") {
      const toRate = map[to];
      if (toRate == null) return null;
      return roundFiat(amount * toRate, to);
    }
    if (to === "SEK") {
      const fromRate = map[from];
      if (fromRate == null || fromRate === 0) return null;
      return roundFiat(amount / fromRate, to);
    }
    const fromRate = map[from];
    const toRate = map[to];
    if (fromRate == null || toRate == null || fromRate === 0) return null;
    return roundFiat(amount * (toRate / fromRate), to);
  }

  const fromRate = from === base ? 1 : map[from];
  const toRate = to === base ? 1 : map[to];
  if (fromRate == null || toRate == null || fromRate === 0) return null;
  return roundFiat(amount * (toRate / fromRate), to);
}

/**
 * Format an amount stored in `fromCurrency` for the floorplan display currency.
 */
export function formatAmountInDisplayCurrency(
  amount: number | null | undefined,
  fromCurrency: string,
  displayCurrency: string,
  ratesArray: Array<{ currency: string; rate: number }> | null | undefined,
  base: string | null | undefined,
  locale?: string,
): string | null {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return null;
  const from = normalizeCurrency(fromCurrency);
  const requested = normalizeCurrency(displayCurrency);
  const converted = convertAmountToDisplayCurrency(
    amount,
    from,
    requested,
    ratesArray,
    base,
  );
  const currency: Currency = converted == null ? from : requested;
  const value = converted == null ? amount : converted;
  const formatted = new Intl.NumberFormat(locale ?? "sv-SE").format(
    Math.round(value),
  );
  return `${formatted} ${currency}`;
}

/**
 * Normalize currency codes from API / UI (e.g. "kr" → SEK).
 */
export function normalizeCurrency(raw: string | null | undefined): Currency {
  const s = raw?.trim() ?? "";
  if (s === "" || s === "kr") return DEFAULT_FLOORPLAN_CURRENCY;
  if (SUPPORTED_CURRENCIES.includes(s as Currency)) return s as Currency;
  return DEFAULT_FLOORPLAN_CURRENCY;
}

/**
 * Format a SEK amount in the given display currency for UI.
 */
export function formatDisplayPrice(
  priceSek: number | null | undefined,
  displayCurrency: string,
  ratesArray: Array<{ currency: string; rate: number }> | null | undefined,
  base: string | null | undefined,
  locale?: string,
): string | null {
  if (typeof priceSek !== "number" || !Number.isFinite(priceSek)) {
    return null;
  }
  const requested = normalizeCurrency(displayCurrency);
  const rate =
    requested === "SEK" || !ratesArray?.length || !base
      ? null
      : getSekToDisplayRate(ratesArray, base, requested);
  // Without a usable rate the amount is still SEK, so label it SEK rather
  // than showing a SEK number with a foreign currency code.
  const currency: Currency = rate == null ? "SEK" : requested;
  const converted =
    rate == null ? priceSek : roundFiat(priceSek * rate, currency);
  // Match prior SEK UI: whole units for readable list totals.
  const amount = new Intl.NumberFormat(locale ?? "sv-SE").format(
    Math.round(converted),
  );
  return `${amount} ${currency}`;
}

/**
 * Short exchange-rate note when display currency is not SEK.
 */
export function formatSekExchangeRateNote(
  displayCurrency: string,
  ratesArray: Array<{ currency: string; rate: number }> | null | undefined,
  base: string | null | undefined,
): string | null {
  const currency = normalizeCurrency(displayCurrency);
  if (currency === "SEK" || !ratesArray?.length || !base) return null;
  const rate = getSekToDisplayRate(ratesArray, base, currency);
  if (rate == null) return null;
  const rateFormatted =
    rate >= 0.01 && rate < 1000 ? rate.toFixed(4) : rate.toExponential(2);
  const label = getCurrencyDisplaySymbol(currency);
  return `1 SEK = ${rateFormatted} ${label === "kr" ? currency : label}`;
}
